import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ErroApi } from "../api/cliente";
import { ProvedorAgua } from "../componentes/Agua";
import css from "./Entrar.module.css";

/** O mínimo do servidor (`usuarios.SENHA_MINIMA`), para avisar antes do envio. */
const SENHA_MINIMA = 8;

/**
 * "Esqueci minha senha" (0143, 15/10): o e-mail de quem esqueceu. A resposta
 * é a mesma para e-mail que tem acesso e que não tem — a tela não diz quem
 * existe —, e por isso a mensagem é condicional.
 */
export function EsqueciSenha() {
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api.esqueci(email);
      setPronto(true);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : "Não foi possível pedir agora. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <ProvedorAgua>
      <div className={css.tela}>
        <form className={css.caixa} onSubmit={(e) => void enviar(e)}>
          <h1>NECTUNO</h1>
          <p className={css.sub}>Criar uma senha nova</p>

          {pronto ? (
            <p className={css.aviso}>
              Se <strong>{email}</strong> tiver acesso ao NECTUNO, enviamos para ele um link
              para criar uma senha nova. O link vale por 1 hora e só pode ser usado uma vez.
              Confira também a caixa de spam.
            </p>
          ) : (
            <>
              <label>
                E-mail do seu acesso
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                />
              </label>
              {erro && <p className={css.erro}>{erro}</p>}
              <button type="submit" disabled={enviando}>
                {enviando ? "Enviando…" : "Enviar o link"}
              </button>
            </>
          )}

          <p className={css.ajuda}>
            <Link to="/entrar">Voltar para entrar</Link>
          </p>
        </form>
      </div>
    </ProvedorAgua>
  );
}

/**
 * A página do link do e-mail: `/redefinir-senha?token=…`. A senha nova, duas
 * vezes; trocada, as sessões abertas caem e a pessoa entra com a nova.
 */
export function RedefinirSenha() {
  const [busca] = useSearchParams();
  const token = busca.get("token") ?? "";
  const [senha, setSenha] = useState("");
  const [repete, setRepete] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (senha.length < SENHA_MINIMA) {
      setErro(`A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`);
      return;
    }
    if (senha !== repete) {
      setErro("As duas senhas não são iguais.");
      return;
    }
    setEnviando(true);
    try {
      await api.redefinir(token, senha);
      setPronto(true);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : "Não foi possível trocar agora. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <ProvedorAgua>
      <div className={css.tela}>
        <form className={css.caixa} onSubmit={(e) => void enviar(e)}>
          <h1>NECTUNO</h1>
          <p className={css.sub}>Criar uma senha nova</p>

          {!token ? (
            <p className={css.erro}>
              Este endereço não tem o código do link. Abra o link do e-mail inteiro, ou peça
              outro.
            </p>
          ) : pronto ? (
            <p className={css.aviso}>Senha trocada. Entre com a senha nova.</p>
          ) : (
            <>
              <label>
                Senha nova
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={SENHA_MINIMA}
                  value={senha}
                  onChange={(e) => setSenha(e.currentTarget.value)}
                />
              </label>
              <label>
                Repita a senha nova
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={repete}
                  onChange={(e) => setRepete(e.currentTarget.value)}
                />
              </label>
              {erro && <p className={css.erro}>{erro}</p>}
              <button type="submit" disabled={enviando}>
                {enviando ? "Trocando…" : "Trocar a senha"}
              </button>
            </>
          )}

          <p className={css.ajuda}>
            {pronto ? (
              <Link to="/entrar">Entrar</Link>
            ) : (
              <>
                <Link to="/esqueci-senha">Pedir outro link</Link> ·{" "}
                <Link to="/entrar">Voltar para entrar</Link>
              </>
            )}
          </p>
        </form>
      </div>
    </ProvedorAgua>
  );
}
